import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"
import { cn } from "@/lib/utils"

// Zamzam Umrah themed calendar - Dark Navy & Gold color scheme
function ZamzamCalendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  const [currentMonth, setCurrentMonth] = React.useState(props.defaultMonth || new Date());

  return (
    <div className={cn("rounded-xl overflow-hidden shadow-xl border border-gray-700", className)}>
      {/* Dark navy header with gold accent */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 px-4 py-3 border-b border-amber-500/30">
        <div className="text-center">
          <div className="text-xs uppercase tracking-wider text-amber-400/80">Select Date</div>
          <div className="text-lg font-bold text-white">
            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </div>
        </div>
      </div>

      <DayPicker
        showOutsideDays={showOutsideDays}
        month={currentMonth}
        onMonthChange={setCurrentMonth}
        className="p-3 bg-white"
        classNames={{
          months: "flex flex-col",
          month: "space-y-2",
          caption: "flex justify-between items-center px-2 mb-2",
          caption_label: "hidden",
          nav: "flex items-center gap-2 w-full justify-between",
          nav_button: cn(
            "h-8 w-8 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center text-gray-700"
          ),
          nav_button_previous: "",
          nav_button_next: "",
          table: "w-full border-collapse",
          head_row: "flex justify-around mb-1",
          head_cell: "w-10 font-semibold text-xs uppercase text-gray-600",
          row: "flex justify-around w-full",
          cell: "p-0.5",
          day: cn(
            "h-10 w-10 p-0 font-normal rounded-full transition-all duration-200 text-sm",
            "hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500",
            "flex items-center justify-center cursor-pointer text-gray-700"
          ),
          day_selected: cn(
            "bg-gradient-to-r from-amber-500 to-amber-600 text-white",
            "font-semibold shadow-md hover:from-amber-600 hover:to-amber-700"
          ),
          day_today: cn(
            "border-2 border-amber-500 text-amber-600 font-semibold",
            "aria-selected:border-transparent"
          ),
          day_outside: "text-gray-300 hover:bg-gray-50",
          day_disabled: "text-gray-300 opacity-50 cursor-not-allowed hover:bg-transparent",
          day_hidden: "invisible",
          ...classNames,
        }}
        components={{
          IconLeft: ({ className: iconClassName, ...iconProps }) => (
            <ChevronLeft className={cn("h-5 w-5", iconClassName)} {...iconProps} />
          ),
          IconRight: ({ className: iconClassName, ...iconProps }) => (
            <ChevronRight className={cn("h-5 w-5", iconClassName)} {...iconProps} />
          ),
        }}
        {...props}
      />
      
      {/* Footer with quick actions */}
      <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 flex justify-between items-center">
        <button 
          onClick={() => {
            const today = new Date();
            setCurrentMonth(today);
          }}
          className="text-sm text-amber-600 hover:text-amber-700 font-medium"
        >
          Today
        </button>
        <span className="text-xs text-gray-400">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'short' })}
        </span>
      </div>
    </div>
  );
}

ZamzamCalendar.displayName = "ZamzamCalendar"

export { ZamzamCalendar }
